import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function GalleryPage() {
  const [activeTab, setActiveTab] = useState('photos');
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [videoForm, setVideoForm] = useState({ url: '', caption: '' });
  const [editingPhotoCaption, setEditingPhotoCaption] = useState(null); // { id, value }
  const [editingVideo, setEditingVideo] = useState(null); // { id, caption, url }

  useEffect(() => {
    fetch('/api/gallery')
      .then(r => r.json())
      .then(data => {
        setPhotos(data.photos || []);
        setVideos(data.videos || []);
      });
  }, []);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    files.forEach(f => formData.append('photos', f));
    try {
      const res = await fetch('/api/admin/gallery/photos', { method: 'POST', body: formData });
      if (res.ok) {
        const newPhotos = await res.json();
        setPhotos(prev => [...prev, ...newPhotos]);
      } else {
        const msg = await res.text();
        alert(`Upload failed: ${msg}`);
      }
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    }
    setUploading(false);
    e.target.value = '';
  };

  const movePhoto = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= photos.length) return;
    const updated = [...photos];
    const aOrder = updated[index].order;
    const bOrder = updated[swapIndex].order;
    await Promise.all([
      fetch(`/api/admin/gallery/photos/${updated[index].id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: bOrder }),
      }),
      fetch(`/api/admin/gallery/photos/${updated[swapIndex].id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: aOrder }),
      }),
    ]);
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    setPhotos(updated);
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    const res = await fetch(`/api/admin/gallery/photos/${id}`, { method: 'DELETE' });
    if (res.ok) setPhotos(prev => prev.filter(p => p.id !== id));
  };

  const savePhotoCaption = async (id, caption) => {
    await fetch(`/api/admin/gallery/photos/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ caption }),
    });
    setPhotos(prev => prev.map(p => p.id === id ? { ...p, caption } : p));
    setEditingPhotoCaption(null);
  };

  const addVideo = async (e) => {
    e.preventDefault();
    const res = await fetch('/api/admin/gallery/videos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(videoForm),
    });
    if (res.ok) {
      const video = await res.json();
      setVideos(prev => [...prev, video]);
      setVideoForm({ url: '', caption: '' });
    } else {
      alert(await res.text());
    }
  };

  const moveVideo = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= videos.length) return;
    const updated = [...videos];
    const aOrder = updated[index].order;
    const bOrder = updated[swapIndex].order;
    await Promise.all([
      fetch(`/api/admin/gallery/videos/${updated[index].id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: bOrder }),
      }),
      fetch(`/api/admin/gallery/videos/${updated[swapIndex].id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: aOrder }),
      }),
    ]);
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    setVideos(updated);
  };

  const deleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    const res = await fetch(`/api/admin/gallery/videos/${id}`, { method: 'DELETE' });
    if (res.ok) setVideos(prev => prev.filter(v => v.id !== id));
  };

  const saveVideoEdit = async (id) => {
    const { caption, url } = editingVideo;
    const res = await fetch(`/api/admin/gallery/videos/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ caption, url }),
    });
    if (res.ok) {
      setVideos(prev => prev.map(v => v.id === id ? { ...v, caption, url } : v));
      setEditingVideo(null);
    } else {
      alert(await res.text());
    }
  };

  const getYouTubeId = (url) => {
    try {
      const u = new URL(url);
      if (u.hostname === 'youtu.be') return u.pathname.slice(1);
      return u.searchParams.get('v') || '';
    } catch { return ''; }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold dark:text-white">Manage Gallery</h1>
        <Link to="/" className="text-blue-500 hover:underline text-sm">← Back to Calendar</Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('photos')}
          className={`px-4 py-2 rounded font-semibold ${activeTab === 'photos' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 dark:text-white'}`}
        >
          Photos ({photos.length})
        </button>
        <button
          onClick={() => setActiveTab('videos')}
          className={`px-4 py-2 rounded font-semibold ${activeTab === 'videos' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 dark:text-white'}`}
        >
          Videos ({videos.length})
        </button>
      </div>

      {activeTab === 'photos' && (
        <div>
          <label className={`inline-block px-4 py-2 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 mb-6 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {uploading ? 'Uploading...' : 'Upload Photos'}
            <input
              type="file" multiple accept="image/*"
              onChange={handlePhotoUpload} disabled={uploading}
              className="hidden"
            />
          </label>

          {photos.length === 0 && <p className="dark:text-gray-400 italic">No photos yet.</p>}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((photo, index) => (
              <div key={photo.id} className="bg-gray-800 rounded overflow-hidden">
                <img src={photo.src} alt={photo.caption} className="w-full object-cover" style={{ aspectRatio: '4/3' }} />
                <div className="p-2">
                  {editingPhotoCaption?.id === photo.id ? (
                    <div className="flex gap-1">
                      <input
                        className="flex-1 text-xs p-1 bg-gray-700 text-white rounded"
                        value={editingPhotoCaption.value}
                        onChange={e => setEditingPhotoCaption(prev => ({ ...prev, value: e.target.value }))}
                      />
                      <button onClick={() => savePhotoCaption(photo.id, editingPhotoCaption.value)} className="text-xs bg-green-600 text-white px-2 py-1 rounded">Save</button>
                      <button onClick={() => setEditingPhotoCaption(null)} className="text-xs bg-gray-600 text-white px-2 py-1 rounded">✕</button>
                    </div>
                  ) : (
                    <p
                      className="text-xs dark:text-gray-300 cursor-pointer hover:text-blue-400 truncate"
                      onClick={() => setEditingPhotoCaption({ id: photo.id, value: photo.caption || '' })}
                    >
                      {photo.caption || <span className="italic text-gray-500">Add caption</span>}
                    </p>
                  )}
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => movePhoto(index, -1)} disabled={index === 0} className="text-xs px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-30">↑</button>
                    <button onClick={() => movePhoto(index, 1)} disabled={index === photos.length - 1} className="text-xs px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-30">↓</button>
                    <button onClick={() => deletePhoto(photo.id)} className="text-xs px-2 py-1 bg-red-700 text-white rounded ml-auto">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'videos' && (
        <div>
          <form onSubmit={addVideo} className="bg-gray-800 rounded p-4 mb-6">
            <h2 className="text-sm font-semibold dark:text-white mb-3">Add Video</h2>
            <div className="flex flex-col gap-2">
              <input
                placeholder="YouTube URL"
                value={videoForm.url}
                onChange={e => setVideoForm(prev => ({ ...prev, url: e.target.value }))}
                className="p-2 bg-gray-700 text-white rounded text-sm"
                required
              />
              <input
                placeholder="Caption (optional)"
                value={videoForm.caption}
                onChange={e => setVideoForm(prev => ({ ...prev, caption: e.target.value }))}
                className="p-2 bg-gray-700 text-white rounded text-sm"
              />
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 self-start">Add</button>
            </div>
          </form>

          {videos.length === 0 && <p className="dark:text-gray-400 italic">No videos yet.</p>}

          <div className="flex flex-col gap-4">
            {videos.map((video, index) => {
              const ytId = getYouTubeId(video.url);
              return (
                <div key={video.id} className="bg-gray-800 rounded p-3 flex gap-3 items-start">
                  {ytId && (
                    <img
                      src={`https://img.youtube.com/vi/${ytId}/default.jpg`}
                      alt="" className="w-24 h-16 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    {editingVideo?.id === video.id ? (
                      <div className="flex flex-col gap-1 mb-1">
                        <input
                          placeholder="YouTube URL"
                          className="text-xs p-1 bg-gray-700 text-white rounded"
                          value={editingVideo.url}
                          onChange={e => setEditingVideo(prev => ({ ...prev, url: e.target.value }))}
                        />
                        <input
                          placeholder="Caption"
                          className="text-xs p-1 bg-gray-700 text-white rounded"
                          value={editingVideo.caption}
                          onChange={e => setEditingVideo(prev => ({ ...prev, caption: e.target.value }))}
                        />
                        <div className="flex gap-1">
                          <button onClick={() => saveVideoEdit(video.id)} className="text-xs bg-green-600 text-white px-2 py-1 rounded">Save</button>
                          <button onClick={() => setEditingVideo(null)} className="text-xs bg-gray-600 text-white px-2 py-1 rounded">✕</button>
                        </div>
                      </div>
                    ) : (
                      <p
                        className="text-xs dark:text-gray-300 cursor-pointer hover:text-blue-400 mb-1 truncate"
                        onClick={() => setEditingVideo({ id: video.id, caption: video.caption || '', url: video.url })}
                      >
                        {video.caption || <span className="italic text-gray-500">Add caption</span>}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 truncate">{video.url}</p>
                    <div className="flex gap-1 mt-2">
                      <button onClick={() => moveVideo(index, -1)} disabled={index === 0} className="text-xs px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-30">↑</button>
                      <button onClick={() => moveVideo(index, 1)} disabled={index === videos.length - 1} className="text-xs px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-30">↓</button>
                      <button onClick={() => deleteVideo(video.id)} className="text-xs px-2 py-1 bg-red-700 text-white rounded ml-auto">Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryPage;
